import React, { useState, useEffect, useCallback } from "react";
import { Container, Box, Typography, Snackbar, Alert, Button } from "@mui/material";
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { useQuery, useMutation } from "@tanstack/react-query";
import { fetchServices, getCurrentBusinessConfig, updateEmployee } from "../../../firebase/firebase-delegate";
import { BusinessConfig, Employee, Service } from "../../../types";
import MyServicesTable from "./MyServicesTable";
import ContactCard from "./ContactCard";
import { EMPLOYEES_DB_REF } from "../../../firebase/firebase-references";
import LoadingSpinner from "../../../components/global/LoadingSpinner";
import { useAdminData } from "../../../contexts/AdminDataContext";
import { useQueryClient } from "@tanstack/react-query";
import { EMPLOYEE_BIO_MAX_LENGTH } from "../../../constants";
import { resolveBookingUxConfig } from "../../../utils/bookingUxConfig";


const MyProfileScreen: React.FC = () => {
    const [profilePicPlaceholder, setProfilePicPlaceholder] = useState<File | undefined>(undefined);
    const [updatedEmployee, setUpdatedEmployee] = useState<Employee | null>(null);
    const [shouldSaveProfilePic, setShouldSaveProfilePic] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });


    // Fetch current employee data
    const {
        currentEmployeeQuery: employeeQuery,
        hydrateAppointmentsQuery,
        scheduleQuery,
        workHoursQuery,
    } = useAdminData();

    // Fetch all services data
    const servicesQuery = useQuery<Service[]>({
        queryKey: ['services'],
        queryFn: () => fetchServices(),
    });
    const businessConfigQuery = useQuery<BusinessConfig | null>({
        queryKey: ['businessConfig'],
        queryFn: () => getCurrentBusinessConfig(),
    });
    const bookingUxConfig = resolveBookingUxConfig(businessConfigQuery.data);
    const isResourceMode = bookingUxConfig.bookingModel === 'resource';

    // Fetch the next 7 days work hours
    // This query will fetch the work hours for the next 7 days based on the employee's schedule
    // and the current date. It will return an array of objects containing the date, day, and hours information.
    // The query will be enabled only if the employee ID and schedule data are available.
    // Work hours and next 7 days tables intentionally hidden (UI-only request)


    // Fetch the employee work hours
    useEffect(() => {
        if (employeeQuery.data) {
            console.log("Employee data:", employeeQuery.data);
            setUpdatedEmployee({ ...employeeQuery.data });
        }
    }, [employeeQuery.data]);


    const handleChangeProfilePic = (newPicture?: File) => {
        setUpdatedEmployee((prev) => {
            if (prev === null) {
                return null;
            }

            // Create a new object to avoid mutating the state directly
            const updatedEmployee = { ...prev };

            // If they updated the picture by only removing the old one,
            // delete the field so updateEmployee knows to remove it
            if (updatedEmployee.profilePictureImagePath && !newPicture) {
                delete updatedEmployee.profilePictureImagePath;
            } else if (newPicture) {
                updatedEmployee.profilePictureImagePath = updatedEmployee.profilePictureImagePath
                    ? updatedEmployee.profilePictureImagePath
                    : (EMPLOYEES_DB_REF + employeeQuery.data?.id + "/profilePicture");
            }
            console.log("check path:", updatedEmployee);

            return updatedEmployee;
        });
        setProfilePicPlaceholder(newPicture);
    };

    const queryClient = useQueryClient();
    const updateEmployeeMutation = useMutation({
        mutationFn: async (nextEmployee: Employee) => {
            if (!employeeQuery.data) {
                throw new Error("Employee data is missing");
            }
            await updateEmployee(employeeQuery.data, nextEmployee, profilePicPlaceholder);
            return nextEmployee;
        },
        onSuccess: (nextEmployee) => {
            if (nextEmployee) {
                queryClient.setQueryData(['currentEmployee'], nextEmployee);
                setUpdatedEmployee(nextEmployee);
            }
            setSnackbar({ open: true, message: 'Changes saved successfully.', severity: 'success' });
        },
        onError: (error: Error) => {
            console.error("Error updating employee: ", error);
            setSnackbar({ open: true, message: error.message || 'Failed to save changes.', severity: 'error' });
        }
    });

    const onSaveProfilePictureChange = useCallback(async () => {
        if (!employeeQuery.data || !updatedEmployee) {
            return;
        }
        await updateEmployeeMutation.mutateAsync({ ...updatedEmployee });
    }, [employeeQuery.data, updatedEmployee, updateEmployeeMutation]);

    // Function to handle profile picture change
    // This function is called when the profile picture is changed
    useEffect(() => {
        if (shouldSaveProfilePic && updatedEmployee) {
            setShouldSaveProfilePic(false); // reset flag
            onSaveProfilePictureChange();   // perform save
        }
    }, [updatedEmployee, shouldSaveProfilePic, onSaveProfilePictureChange]);

    const derivedScheduleLoading =
        scheduleQuery.isLoading ||
        scheduleQuery.isFetching ||
        hydrateAppointmentsQuery.isLoading ||
        hydrateAppointmentsQuery.isFetching;

    const isLoading =
        employeeQuery.isLoading ||
        updateEmployeeMutation.isPending ||
        servicesQuery.isLoading ||
        businessConfigQuery.isLoading ||
        workHoursQuery.isLoading ||
        derivedScheduleLoading;
    const isError =
        employeeQuery.isError ||
        updateEmployeeMutation.isError ||
        servicesQuery.isError ||
        businessConfigQuery.isError ||
        workHoursQuery.isError ||
        scheduleQuery.isError ||
        hydrateAppointmentsQuery.isError;

    // Work hours and next 7 days tables intentionally hidden (UI-only request)

    const handleCopyBookNowLink = useCallback(async () => {
        if (!employeeQuery.data?.id) {
            setSnackbar({ open: true, message: 'Unable to generate your booking link.', severity: 'error' });
            return;
        }

        const bookNowLink = `${window.location.origin}/book-now?employee=${encodeURIComponent(employeeQuery.data.id)}`;

        try {
            await navigator.clipboard.writeText(bookNowLink);
            setSnackbar({ open: true, message: 'Book now link copied.', severity: 'success' });
        } catch (error) {
            console.error('Failed to copy booking link:', error);
            setSnackbar({ open: true, message: 'Failed to copy book now link.', severity: 'error' });
        }
    }, [employeeQuery.data?.id]);


    if (isLoading) {
        return (
            <Container sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
                <LoadingSpinner />
            </Container>
        );
    }

    if (!employeeQuery.data || !servicesQuery.data || isError) {
        return <Typography variant="h6" align="center">Error loading profile</Typography>;
    }

    // Filter services that include the current employee ID
    const assignedServices = servicesQuery.data.filter(service => service.employees.includes(employeeQuery.data.id));

    const onSaveContactInfo = (
        updatedFirstName: string,
        updatedLastName: string,
        updatedEmail: string,
        updatedPhone: string,
        updatedBio: string,
        updatedSocials: { instagram?: string; facebook?: string; twitter?: string }
    ) => {
        try {
            if (updatedEmployee && employeeQuery.data) {
                const nextEmployee: Employee = {
                    ...updatedEmployee,
                    firstName: updatedFirstName,
                    lastName: updatedLastName,
                    email: updatedEmail,
                    phone: updatedPhone,
                };
                const trimmedBio = updatedBio.trim().slice(0, EMPLOYEE_BIO_MAX_LENGTH);
                if (trimmedBio.length === 0) {
                    delete nextEmployee.bio;
                } else {
                    nextEmployee.bio = trimmedBio;
                }
                if (!updatedSocials.instagram && !updatedSocials.facebook && !updatedSocials.twitter) {
                    delete nextEmployee.socials;
                } else {
                    const sanitizedSocials: { instagram?: string; facebook?: string; twitter?: string } = {};
                    if (updatedSocials.instagram) {
                        sanitizedSocials.instagram = updatedSocials.instagram;
                    }
                    if (updatedSocials.facebook) {
                        sanitizedSocials.facebook = updatedSocials.facebook;
                    }
                    if (updatedSocials.twitter) {
                        sanitizedSocials.twitter = updatedSocials.twitter;
                    }
                    nextEmployee.socials = sanitizedSocials;
                }
                setUpdatedEmployee(nextEmployee);
                updateEmployeeMutation.mutate(nextEmployee);
            }
        } catch (error) {
        }
    }

    const handleChangeProfilePicClick = (newPicture?: File) => {
        handleChangeProfilePic(newPicture);
        setShouldSaveProfilePic(true);
    };

    return (
        <Container>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 4, width: "100%", gap: 3 }}>
                {!isResourceMode && (
                    <Box sx={{ width: "100%", maxWidth: 560, display: "flex", justifyContent: "center" }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<ContentCopyRoundedIcon />}
                            onClick={handleCopyBookNowLink}
                            sx={{ borderRadius: 999, px: 3, py: 1.25 }}
                        >
                            Share Your Book Now Link
                        </Button>
                    </Box>
                )}
                <ContactCard
                    firstName={employeeQuery.data.firstName}
                    lastName={employeeQuery.data.lastName}
                    email={employeeQuery.data.email || ""}
                    phone={employeeQuery.data.phone || ""}
                    bio={employeeQuery.data.bio || ""}
                    socials={employeeQuery.data.socials}
                    profileImagePath={employeeQuery.data.profilePictureImagePath}
                    onSave={onSaveContactInfo}
                    onImageChange={handleChangeProfilePicClick}
                />

                <MyServicesTable services={assignedServices} admin={employeeQuery.data.admin} />

                {/*
                <Box
                    sx={{
                        mt: 3,
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 3,
                        width: "100%",
                    }}
                >
                    <WorkHoursTable
                        title="Next 7 Days"
                        workHours={nextSevenDaysWorkHours}
                        navigateLocation={"/admin/schedule-hub"} />
                    <WorkHoursTable title={"Default Work Hours"} workHours={formattedWorkHours} navigateLocation={"/admin/edit-work-hours"} />
                </Box>
                */}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default MyProfileScreen;
